import {
    getConsentDecision,
    hasAnalyticsConsent,
    initializeConsent,
    subscribeConsent,
} from '@utils/cookie-consent';

type Ym = ((...args: unknown[]) => void) & {
    a?: unknown[][];
};

declare global {
    interface Window {
        ym?: Ym;
        dataLayer?: Record<string, unknown>[];
    }
}

const TAG_SCRIPT_ID = 'yandex-metrika-tag';
const TAG_SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js';
const METRIKA_COOKIE_PREFIX = /^_?ym/;
const LEGACY_GOOGLE_COOKIE_PREFIX = /^(_ga(?:_|$)|_gid$|_gat(?:_|$))/;

let counterId: number | null = null;
let active = false;
let initializedId: number | null = null;
let tagState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
let loadStarted = false;
let lastHitUrl: string | null = null;
let reloading = false;
let createdDataLayer = false;
let consentSubscriptionAttached = false;
let linkListenerAttached = false;
let ownEvents: Record<string, unknown>[] = [];

export function normalizeCounterId(value: unknown): number | null {
    if (typeof value !== 'number') return null;
    if (!Number.isSafeInteger(value) || value <= 0) return null;
    return value;
}

function getYmQueue(): Ym {
    if (window.ym !== undefined) return window.ym;

    const queue: Ym = (...args) => {
        (queue.a ??= []).push(args);
    };
    window.ym = queue;
    return queue;
}

function dataLayer(): Record<string, unknown>[] {
    if (window.dataLayer !== undefined) return window.dataLayer;
    createdDataLayer = true;
    const layer: Record<string, unknown>[] = [];
    window.dataLayer = layer;
    return layer;
}

function removeOwnEvents(): void {
    if (ownEvents.length === 0) return;

    const layer = window.dataLayer;
    if (Array.isArray(layer)) {
        for (let index = layer.length - 1; index >= 0; index--) {
            if (ownEvents.includes(layer[index])) layer.splice(index, 1);
        }
        if (createdDataLayer && layer.length === 0) {
            delete window.dataLayer;
            createdDataLayer = false;
        }
    }

    ownEvents = [];
}

function removePendingCommands(): void {
    const queue = window.ym?.a;
    if (queue === undefined || queue.push !== Array.prototype.push) return;

    if (counterId === null) return;

    for (let index = queue.length - 1; index >= 0; index--) {
        const command = queue[index];
        if (Array.isArray(command) && Number(command[0]) === counterId) queue.splice(index, 1);
    }
}

function domainVariants(): (string | null)[] {
    const hostname = window.location.hostname;
    const variants: (string | null)[] = [null, hostname, `.${hostname}`];
    const labels = hostname.split('.');

    for (let index = 1; index < labels.length - 1; index++) {
        const domain = labels.slice(index).join('.');
        variants.push(domain, `.${domain}`);
    }

    return Array.from(new Set(variants));
}

function pathVariants(): string[] {
    const paths = new Set<string>(['/']);
    const segments = window.location.pathname.split('/').filter(Boolean);
    let current = '';

    segments.forEach((segment) => {
        current += `/${segment}`;
        paths.add(current);
        paths.add(`${current}/`);
    });

    return Array.from(paths);
}

function cookieNames(): string[] {
    try {
        return document.cookie
            .split(';')
            .map((cookie) => cookie.trim().split('=')[0])
            .filter(
                (name) =>
                    METRIKA_COOKIE_PREFIX.test(name) || LEGACY_GOOGLE_COOKIE_PREFIX.test(name),
            );
    } catch {
        return [];
    }
}

function expireCookie(name: string, domain: string | null, path: string): void {
    const domainAttribute = domain === null ? '' : `; domain=${domain}`;
    document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainAttribute}`;
}

function clearAnalyticsCookies(): void {
    const names = cookieNames();
    const domains = domainVariants();
    const paths = pathVariants();

    names.forEach((name) => {
        domains.forEach((domain) => {
            paths.forEach((path) => {
                try {
                    expireCookie(name, domain, path);
                } catch {
                    /* A denied cookie write must not block revocation. */
                }
            });
        });
    });
}

function removeTagScript(): void {
    const script = document.getElementById(TAG_SCRIPT_ID);
    if (script instanceof HTMLScriptElement) {
        script.onload = null;
        script.onerror = null;
        script.remove();
    }
}

function reloadPage(): void {
    if (reloading || typeof window === 'undefined') return;
    reloading = true;
    try {
        window.location.reload();
    } catch {
        reloading = false;
    }
}

function resetState(): void {
    active = false;
    initializedId = null;
    tagState = 'idle';
    loadStarted = false;
    lastHitUrl = null;
}

export function isAnalyticsActive(): boolean {
    return active && hasAnalyticsConsent() && counterId !== null;
}

export function pushAnalyticsEvent(event: Record<string, unknown>): void {
    if (!isAnalyticsActive()) return;

    try {
        const layer = dataLayer();
        const reset = { ecommerce: null };
        const entry = { event, ...event };
        ownEvents.push(reset, entry);
        layer.push(reset, entry);
    } catch {
        /* Analytics transport must never break a business action. */
    }
}

function linkEventName(href: string): 'click_email' | 'click_tele' | null {
    const normalized = href.trim().toLowerCase();
    if (normalized.startsWith('mailto:')) return 'click_email';
    if (normalized.startsWith('tel:')) return 'click_tele';
    return null;
}

function handleAnalyticsLinkClick(event: MouseEvent): void {
    if (!isAnalyticsActive()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    const eventName = linkEventName(anchor.getAttribute('href') ?? '');
    if (eventName !== null) pushAnalyticsEvent({ event: eventName });
}

function attachLinkListener(): void {
    if (typeof document === 'undefined' || linkListenerAttached) return;
    linkListenerAttached = true;
    document.addEventListener('click', handleAnalyticsLinkClick, true);
}

function detachLinkListener(): void {
    if (typeof document === 'undefined' || !linkListenerAttached) return;
    linkListenerAttached = false;
    document.removeEventListener('click', handleAnalyticsLinkClick, true);
}

export function stopAnalytics(options: { reload?: boolean } = {}): void {
    if (typeof window === 'undefined') return;

    const shouldReload = options.reload !== false && loadStarted;
    const id = counterId;
    const hadAnalytics = active || loadStarted || initializedId !== null;

    active = false;
    removeOwnEvents();
    removePendingCommands();
    clearAnalyticsCookies();
    detachLinkListener();

    if (tagState === 'loaded' && window.ym !== undefined) {
        if (id !== null) window.ym?.(id, 'destruct');
    }

    removeTagScript();
    if (hadAnalytics && window.ym !== undefined) delete window.ym;
    resetState();

    if (shouldReload) reloadPage();
}

function startTagScript(): void {
    if (tagState !== 'idle' && tagState !== 'error') return;
    if (document.getElementById(TAG_SCRIPT_ID) !== null) {
        tagState = 'loading';
        loadStarted = true;
        return;
    }

    loadStarted = true;
    tagState = 'loading';
    const script = document.createElement('script');
    script.id = TAG_SCRIPT_ID;
    script.async = true;
    script.src = TAG_SCRIPT_SRC;
    script.onload = () => {
        tagState = 'loaded';
    };
    script.onerror = () => {
        tagState = 'error';
    };
    document.head.appendChild(script);
}

function startAnalytics(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (reloading) return;
    if (!hasAnalyticsConsent() || counterId === null) return;

    active = true;
    const ym = getYmQueue();
    attachLinkListener();
    startTagScript();

    if (initializedId !== counterId) {
        initializedId = counterId;
        ym(counterId, 'init', {
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            webvisor: true,
            ecommerce: 'dataLayer',
        });
    }

    if (lastHitUrl === null) lastHitUrl = window.location.href;
}

function handleConsentChange(decision: string | null): void {
    if (decision === 'accepted') {
        startAnalytics();
    } else {
        stopAnalytics();
    }
}

export function configureAnalytics(value: unknown): void {
    if (typeof window === 'undefined') return;

    initializeConsent();
    if (!consentSubscriptionAttached) {
        consentSubscriptionAttached = true;
        subscribeConsent(handleConsentChange);
    }

    /* The old document must stay inert until the pending reload replaces it. */
    if (reloading) return;

    const next = normalizeCounterId(value);
    if (next === counterId) {
        if (getConsentDecision() === 'accepted') startAnalytics();
        else if (active) stopAnalytics();
        return;
    }

    if (active || loadStarted) {
        stopAnalytics();
        /* A changed ID must never initialize in the old document. The reload
           above is the only supported hand-off; the next document reads the
           new value from settings. */
        if (reloading) return;
    }

    counterId = next;

    if (getConsentDecision() === 'accepted') startAnalytics();
}

export function trackPageView(url = window.location.href): void {
    if (typeof window === 'undefined') return;
    if (!active || !hasAnalyticsConsent() || counterId === null) return;

    const absoluteUrl = new URL(url, window.location.origin).href;
    if (absoluteUrl === lastHitUrl) return;

    lastHitUrl = absoluteUrl;
    window.ym?.(counterId, 'hit', absoluteUrl);
}

export function getConfiguredCounterId(): number | null {
    return counterId;
}
